import { complianceIssues } from '@/apollo/queries/reports/complianceIssues.gql'
import { defineComponent } from 'vue'
import { GraphqlQueryResponse } from '~/types/apolloTypes'
import { ReportPageT } from '~/types/reportTypes'
import {
  ComplianceIssue,
  ComplianceIssueOccurrence,
  ReportLevel,
  ReportStatus
} from '~/types/types'
import ReportMixin from './reportMixin'

/**
 * Mixin for compliance report utilities
 */
export default defineComponent({
  name: 'ComplianceReportMixin',
  mixins: [ReportMixin],
  computed: {
    compliancePassed(): boolean {
      return this.report?.status === ReportStatus.Passing
    },
    chartColors(): Array<string> {
      return this.compliancePassed ? ['juniper-500'] : ['cherry-500']
    }
  },
  methods: {
    /**
     * Fetch and set compliance issues of a compliance report.
     *
     * @param {ReportLevel} level
     * @param {string} objectId
     * @param {ReportPageT} reportKey
     */
    async fetchComplianceIssues(
      level: ReportLevel,
      objectId: string,
      reportKey: ReportPageT,
      refetch = false
    ) {
      if (objectId && level && reportKey) {
        const fetchSeverityMap = [ReportPageT.OWASP_TOP_10, ReportPageT.MISRA_C].includes(reportKey)

        this.complianceIssuesLoading = true
        try {
          const response = (await this.$fetchGraphqlData(
            complianceIssues,
            {
              level,
              objectId,
              reportKey,
              fetchSeverityMap
            },
            refetch
          )) as GraphqlQueryResponse

          this.complianceIssueList = response.data?.complianceIssues as Array<ComplianceIssue>
          this.complianceIssuesSeverityMap = response.data
            ?.complianceIssuesSeverityMap as ComplianceIssueOccurrence
        } catch (e) {
          this.$logErrorAndToast(
            e as Error,
            'Unable to fetch security issues list, please contact support.'
          )
        } finally {
          this.complianceIssuesLoading = false
        }
      }
    },

    formatMisraIssueTag(tag: string): string {
      return tag
        .toLowerCase() // convert to lowercase
        .replace(/[\s.]+/g, '-') // replace spaces and dots with hyphens
    }
  }
})
